import Form, { FormComponentProps } from 'antd/lib/form';
import Select from 'antd/lib/select';
import * as React from 'react';
import { useEffect, useState } from 'react';
import withStyles from 'react-jss';
import { getDocumentTemplates } from 'src/api/shipmentExecution';
import { StylesProps } from 'src/Library/Types';
import Asterisk from 'src/components/common/Asterisk';
import message from 'antd/lib/message';
import { DocumentDetailsDto, DocumentDetailsType } from '../../../SE.interfaces';
import DocumentTemplate from './DocumentTemplate';

interface SEUploadDocumentsTaskDetailsProps extends
  StylesProps<ReturnType<typeof styles>>, FormComponentProps {
  visible: boolean;
  sourceObjectId: string;
  isTemplateMandatory?: boolean;
  taskId: string;
  taskUpdateActions: 'UPDATE' | undefined;
  setIsUpdateDisabled: (isUpdateDisabled: boolean) => void;
  setApiCallObject: (val: DocumentDetailsDto | {}) => void;
  taskDetails;
  enableMultipleDuplicateValues?: boolean;
}

export type documentTemplateType = {
  key: string;
  id: string;
  label: string;
  isMandatory?: boolean;
  dropdownLabel: string;
};

const styles = theme => ({
  header: {
    borderTop: '1px #FAF6EF solid',
    borderBottom: '1px #FAF6EF solid',
    backgroundColor: '#FCFAF4',
    height: 56,
    display: 'flex',
    alignItems: 'center',
    width: '102%',
    marginLeft: '-9px',
    marginBottom: theme.spacing.lg,
  },
  blueLink: {
    color: theme.colors.primaryDark,
    fontSize: theme.fontSize.small,
    fontWeight: theme.fontWeight.semibold,
  },
  documentSelect: {
    width: 300,
    height: 30,
    marginLeft: theme.spacing.md,
  },
  documentAndFields: {
    padding: `0px ${theme.spacing.ms}px`,
  },
});

const SEUploadDocumentsTaskDetails: React.FunctionComponent<SEUploadDocumentsTaskDetailsProps> = ({
  form,
  classes,
  visible,
  taskDetails,
  sourceObjectId,
  isTemplateMandatory,
  taskId,
  taskUpdateActions,
  setApiCallObject, /** set this as empty object if not required */
  setIsUpdateDisabled,
  enableMultipleDuplicateValues
}) => {

  const [templateOptions, setTemplateOptions] = useState<documentTemplateType[]>();
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]); // array of template

  const [documentDetails, setDocumentDetails] = useState<DocumentDetailsType[]>([]);
  const [submitClicked, setSubmitClicked] = useState<Boolean>(false);

  const loadTemplates = async () => {
    const response = await getDocumentTemplates({ id: sourceObjectId, taskId });
    if (response.isSuccess) {
      const options = (response.data || []).map(template => {
        return {
          key: template.id+template.name,	/** template.id is not unique */
          id: template.id,
          label: template.name,
          dropdownLabel: template.name + (template?.isMandatory ? ' *' : ''),
          isMandatory: !!template?.isMandatory
        }
      });
      setTemplateOptions(options);
    }
  }

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (taskUpdateActions) {
      switch (taskUpdateActions) {
        case 'UPDATE': {
          handleSubmit()
          break;
        }
      }
    }
  }, [taskUpdateActions]);

  const isTemplatesMandatory = (templateKey?) => {
    const isMandatory = (templateKey || selectedTemplates)?.some(elm => {
      const templateName = elm?.replace(/[0-9]*/, '')?.trim();
      return !!templateOptions?.filter(elm => elm.label === templateName && elm?.isMandatory)?.length;
    });
    return isMandatory
  }

  const createApiCallObject = (): DocumentDetailsDto => {
    return {
      id: sourceObjectId,
      taskId,
      params: documentDetails,
    };
  }

  const isNewDocumentUploaded = () => {
    if (documentDetails.length > 0){
      for (const document of documentDetails){
        if (!document.documentDetails?.[0]?.url){
          return false;
        }
      }
      return true;
    }

    return false;
  }

  const areDocumentsAlreadyPresent = () => {
    return taskDetails?.docNodes?.length;
  }

  const handleSubmit = async () => {
    /**
     * if only Attachment template is present AND no new document is uploaded
     * */
    setSubmitClicked(prevState => !prevState);
    if (
      (!templateOptions || templateOptions.length <= 1) ||
      (!isNewDocumentUploaded() && areDocumentsAlreadyPresent() && !isTemplatesMandatory())
    ) {
      setApiCallObject({});
      return;
    }

    /**
     * If no documents are selected and none are mandatory, proceed with submission
     */
        if (!selectedTemplates.length && !isTemplatesMandatory()) {
          setApiCallObject({});
          return;
        }

    form.validateFields(async (err, values) => {
      if (err) {
        message.error('Please fill the required fields');
        setSubmitClicked(prevState => !prevState);
        return;
      }
    });

  };

  useEffect(() => {
    if (submitClicked && (!isTemplateMandatory || selectedTemplates.length) && selectedTemplates?.length === documentDetails?.length) {
      const isError = !isNewDocumentUploaded() && isTemplatesMandatory();
      if (isError) {
        message.error('Please upload the required document');
        setSubmitClicked(prevState => !prevState);
        setDocumentDetails([]);
        return;
      }
      
      const apiCallObject = createApiCallObject();
      setApiCallObject(apiCallObject);

      // To handle resubmission due to error
      setSubmitClicked(prevState => !prevState);
      setDocumentDetails([]);
    }
  }, [submitClicked, documentDetails]);

  const handleAddNewTemplate = (templateKey: string) => {
    setSubmitClicked(false);
    const uniqueSelectedTemplates = new Set([...selectedTemplates, templateKey]);
    setSelectedTemplates(Array.from(uniqueSelectedTemplates));
  }

  const handleDeleteTemplate = (templateKey: string) => {
    setSelectedTemplates(selectedTemplates?.filter(key => key !== templateKey));
  }

  const renderDocumentTemplate = (selectedTemplateKey: string) => {
    return (
      <DocumentTemplate
        submitClicked={submitClicked}
        form={form}
        sourceObjectId={sourceObjectId}
        templateOptions={templateOptions}
        taskUpdateActions={taskUpdateActions}
        selectedTemplateKey={selectedTemplateKey}
        setIsUpdateDisabled={setIsUpdateDisabled}
        handleDeleteTemplate={handleDeleteTemplate}
        setDocumentDetails={val => setDocumentDetails(val)}
        enableMultipleDuplicateValues={enableMultipleDuplicateValues}
      />
    );
  }

  const renderSelectDocumentHeader = () => {
    const hasMandatoryTemplate = templateOptions?.some(template => template.isMandatory);
    return (
      <>
        <Select
          placeholder="Select Document to Upload"
          className={classes.documentSelect}
          onChange={(templateKey: any) => {
            if (templateKey) {
              handleAddNewTemplate(templateKey?.toString());
            }
          }}
          value={undefined}
        >
          {templateOptions?.map(template => (
            <Select.Option value={template.key} key={template.key}>{template.dropdownLabel}</Select.Option>
          ))}
        </Select>
        {hasMandatoryTemplate ? <Asterisk /> : null}
      </>
    );
  }

  if (!visible) {
    return null;
  }

  if (!templateOptions || templateOptions?.length <= 1) {
    return null;
  }

  return (
    <div>
      <div className={classes.header}>
        {renderSelectDocumentHeader()}
      </div>
      <div className={classes.documentAndFields}>
        {selectedTemplates.map(templateKey => renderDocumentTemplate(templateKey))}
        {/* {renderNewDocument()} */}
      </div>
    </div>
  );
}

export default withStyles(styles)(Form.create({})(SEUploadDocumentsTaskDetails));